// @flow
import { PDFFillerContext } from 'pages/Contexts/PdfFillerContext'
import { useUpdateResponseQueue } from 'queries/response_queue/useUpdateResponseQueue'
import * as React from 'react'
import type { Response } from 'types/response'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import PageFooter from 'ui/molecules/PageFooter'
import { ZentakeContext } from 'utils/ZentakeContext'

type Props = {
  onPreviousClick: () => void,
  onNextClick: () => void,
  onSubmit: (
    config?: ?{ validate: boolean, submit?: boolean, send?: boolean }
  ) => Promise<?Response>,
  onSendQueue: () => Promise<void>,
}

function DocumentSectionFooter({ onSubmit, onSendQueue }: Props): React.Node {
  const { isSubmitted, submitting, viewMode } =
    React.useContext(PDFFillerContext)
  const { isLoading: isUpdateResponseQueueLoading } = useUpdateResponseQueue()

  const { setAlertConfig } = React.useContext(ZentakeContext)

  const handleClick = React.useCallback(async () => {
    if (viewMode === 'preview') return

    const confirmation =
      viewMode === 'office-use'
        ? true
        : await new Promise((resolve) => {
            setAlertConfig({
              title: 'Are you sure you want to submit?',
              description:
                'After submitting you will not be able to make any additional changes.',
              buttons: [
                {
                  title: 'Submit',
                  style: 'primary',
                  onPress: () => resolve(true),
                },
                {
                  title: 'Cancel',
                  onPress: () => resolve(false),
                },
              ],
              canDismiss: false,
            })
          })

    if (confirmation) {
      await onSubmit()
    }
  }, [onSubmit, setAlertConfig, viewMode])

  const submitButtonLabel = React.useMemo(() => {
    if (viewMode !== 'office-use') {
      return 'Submit'
    }

    if (isSubmitted) {
      return 'Save'
    }

    return 'Send'
  }, [isSubmitted, viewMode])

  if (viewMode === 'view') return null

  return (
    <PageFooter>
      <Flex $flexDirection={'row'}>
        {viewMode === 'office-use' && (
          <Button
            $primary
            className="rounded-r-none"
            disabled={
              submitting ||
              isUpdateResponseQueueLoading ||
              viewMode === 'preview'
            }
            onClick={onSendQueue}
          >
            Queue
          </Button>
        )}

        <Button
          $primary
          className={viewMode === 'office-use' ? 'rounded-l-none' : ''}
          disabled={
            submitting || isUpdateResponseQueueLoading || viewMode === 'preview'
          }
          onClick={handleClick}
        >
          {submitButtonLabel}
        </Button>
      </Flex>
    </PageFooter>
  )
}

export default DocumentSectionFooter
